import PropTypes from 'prop-types';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Collapse, Divider, List, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// project imports
import NavItem from '../NavItem';
import NavCollapse from '../NavCollapse';

// ==============================|| SIDEBAR MENU LIST GROUP ||============================== //

// Recursively check if any descendant menu item's url matches the current pathname.
const containsActiveUrl = (node, pathname) => {
    if (!node) return false;
    if (typeof node.url === 'string' && node.url) {
        // exact match OR pathname is a sub-route of url
        if (pathname === node.url || pathname.startsWith(node.url + '/')) return true;
    }
    if (Array.isArray(node.children)) {
        return node.children.some((child) => containsActiveUrl(child, pathname));
    }
    return false;
};

const NavGroup = ({ item }) => {
    const theme = useTheme();
    const { pathname } = useLocation();
    const hasTitle = !!item.title;

    // Auto-expanded if any descendant route matches the current URL
    const isActiveGroup = useMemo(() => containsActiveUrl(item, pathname), [item, pathname]);

    // User can manually override; null means "use auto" (active=expanded, inactive=collapsed)
    const [manualOverride, setManualOverride] = useState(null);

    // Reset manual override whenever the active group changes (so navigation always wins)
    useEffect(() => {
        setManualOverride(null);
    }, [isActiveGroup]);

    const expanded = manualOverride !== null ? manualOverride : isActiveGroup;
    const collapsed = !expanded;

    const toggle = useCallback(() => {
        setManualOverride((prev) => {
            const current = prev !== null ? prev : isActiveGroup;
            return !current;
        });
    }, [isActiveGroup]);

    // menu list collapse & items
    const items = item.children?.map((menu) => {
        switch (menu.type) {
            case 'collapse':
                return <NavCollapse key={menu.id} menu={menu} level={1} />;
            case 'item':
                return <NavItem key={menu.id} item={menu} level={1} />;
            default:
                return (
                    <Typography key={menu.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    return (
        <>
            <List
                subheader={
                    hasTitle && (
                        <Box
                            onClick={toggle}
                            role="button"
                            aria-expanded={expanded}
                            sx={{
                                cursor: 'pointer',
                                userSelect: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1.25,
                                py: 0.5,
                                mt: 0.5,
                                borderRadius: 1,
                                transition: 'background-color 0.15s ease',
                                '&:hover': { backgroundColor: theme.palette.action.hover }
                            }}
                        >
                            <Box>
                                <Typography variant="caption" sx={{ ...theme.typography.menuCaption, m: 0 }} display="block">
                                    {item.title}
                                </Typography>
                                {item.caption && (
                                    <Typography variant="caption" sx={{ ...theme.typography.subMenuCaption }} display="block">
                                        {item.caption}
                                    </Typography>
                                )}
                            </Box>
                            {collapsed ? (
                                <ExpandMoreIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                            ) : (
                                <ExpandLessIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                            )}
                        </Box>
                    )
                }
            >
                <Collapse in={expanded || !hasTitle} timeout="auto" unmountOnExit>
                    <Box>{items}</Box>
                </Collapse>
            </List>

            {/* group divider */}
            <Divider sx={{ mt: 0.25, mb: 1.25 }} />
        </>
    );
};

NavGroup.propTypes = {
    item: PropTypes.object
};

export default NavGroup;
